// MEO landmark ads (Phase 1, Step 4-3 PR 3 / 案 A foundation, 2026-05-01):
// Mapbox 共通プレビューコンポーネント。
//
// 用途:
// - read-only mode: 緯度/経度を pin で表示するだけ (form preview など)
// - draggable mode: pin をドラッグして座標を逆方向に親に伝える (form picker)
//
// 必須 env: REACT_APP_MAPBOX_PUBKEY (publishable token、pk.* で始まる)。
// なければ警告 box を表示するフォールバック (build は通る)。

import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

// アクセストークンはモジュールロード時に 1 度だけセット。
// process.env は CRA の build time に置換されるので、ランタイムで未定義でも安全。
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_PUBKEY ?? '';
if (MAPBOX_TOKEN) {
    mapboxgl.accessToken = MAPBOX_TOKEN;
}

export interface MapPreviewProps {
    latitude: number,
    longitude: number,
    zoom?: number,
    height?: string,
    /** ドラッグして座標を変更可能にする (form picker 用) */
    draggable?: boolean,
    /** draggable 時、ドラッグ終了で呼ばれる */
    onMove?: (lat: number, lng: number) => void
}

const DEFAULT_ZOOM = 13;
const DEFAULT_HEIGHT = '240px';

export default function MapPreview({
    latitude,
    longitude,
    zoom = DEFAULT_ZOOM,
    height = DEFAULT_HEIGHT,
    draggable = false,
    onMove
}: MapPreviewProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const markerRef = useRef<mapboxgl.Marker | null>(null);

    // map 初期化 (mount 時のみ)。token が無ければ初期化しない。
    useEffect(() => {
        if (!MAPBOX_TOKEN) return;
        if (!containerRef.current || mapRef.current) return;

        const map = new mapboxgl.Map({
            container: containerRef.current,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: [longitude, latitude],
            zoom
        });
        mapRef.current = map;

        const marker = new mapboxgl.Marker({ draggable })
            .setLngLat([longitude, latitude])
            .addTo(map);
        markerRef.current = marker;

        if (draggable && onMove) {
            marker.on('dragend', () => {
                const lngLat = marker.getLngLat();
                onMove(lngLat.lat, lngLat.lng);
            });
        }

        return () => {
            map.remove();
            mapRef.current = null;
            markerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // props 更新で marker / center 追従 (親側が値を変えた時)
    useEffect(() => {
        if (!mapRef.current || !markerRef.current) return;
        const lngLat: [number, number] = [longitude, latitude];
        markerRef.current.setLngLat(lngLat);
        // 大きく動いた場合のみ center 追従。微小ドラッグの自己ループを避けるため。
        const center = mapRef.current.getCenter();
        const distance = Math.hypot(center.lat - latitude, center.lng - longitude);
        if (distance > 0.01) {
            mapRef.current.jumpTo({ center: lngLat });
        }
    }, [latitude, longitude]);

    // 2026-05-01: zoom prop 変更追従 (LandmarkFormSheet で geolocation 解決時に
    // LOADING_ZOOM → PIN_ZOOM へ遷移するケースに対応)。
    useEffect(() => {
        if (!mapRef.current) return;
        const currentZoom = mapRef.current.getZoom();
        if (Math.abs(currentZoom - zoom) > 0.01) {
            mapRef.current.setZoom(zoom);
        }
    }, [zoom]);

    // token が無い時は警告 box
    if (!MAPBOX_TOKEN) {
        return (
            <div
                className='border border-orange-300 bg-orange-50 text-orange-800 text-sm rounded-md p-3 flex items-center gap-2'
                style={{ height }}
            >
                <span className='font-bold'>⚠️ Map disabled</span>
                <span>
                    REACT_APP_MAPBOX_PUBKEY が設定されていません。.env / GitHub Secret (ENV_FILE_PRODUCTION) に追記してください。
                </span>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className='rounded-md overflow-hidden border'
            style={{ height }}
        />
    );
}
